/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';

import { BehaviorSubject } from 'rxjs';
import moment from 'moment-timezone';

import { safeDivide } from '@Core/utils';
import { JournodexRegionsService } from '@Common/Journodex/services/journodex-regions.service';
import { ReportsSeriesDataMapperService } from '@Common/Reports/services/reports-series-data-mapper.service';
import { ReportsSeriesMapperService } from '@Common/Reports/services/reports-series-mapper.service';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { IndividualReportSeriesConfig } from '@Monitor/Core/services/monitoring-dashboard-search.service';
import { MonitoringRoutingService } from '@Monitor/Core/services/monitoring-routing.service';
import { MADChartsService,
    MADMapChartCountryNeoFacetRequest,
} from '@Monitor/Shared/services/mad-charts.service';
import { getSentimentMapper } from '../../../../../Common/Reports/components/ChartDrillDownDialog/chart-drill-down-dialog.component';
import { MADSentimentSeriesTypesEnum } from '../../../../../Monitor/Shared/enums/mad-series-types.enum';
import { MentionSearchFacetTypesEnum } from '../../../../../api/mentions/enum/mention-search-facet-types.enum';
import { ReportSeriesFacetGeneratorEnum } from '../../../../../Common/Reports/models/report-series-facet-generator.enum';
import { SearchCriteriaQuerySentimentsEnum } from '../../../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { ChartMetricsEnum } from '../../../../../Common/Reports/enums/chart-metrics.enum';
import { NeoFacetTermFieldApiEnum } from '../../../../../api/mentions/enum/neo-facet-term-field-api.enum';
import { ReportsNeoFacetKeys } from '../../../../../Common/Reports/enums/reports-neo-facet-keys.enum';
import { BucketResponseModel } from '../../../../../api/mentions/model/bucket-response.model';
import { MentionSearchCriteriaModel } from '../../../../../Monitor/Shared/models/mention-search-criteria.model';
import { SentimentModel } from '../../../../../Monitor/Shared/models/sentiment.model';
import { MediaTypeModel } from '../../../../../Monitor/Shared/models/media-type.model';
import { MediaTypeEnum } from '../../../../../Common/Basic/enums/media-type.enum';
import { SortOrderEnum } from '../../../../../api/shared/enums/sort-order.enum';
import { SearchCriteriaSortFieldEnum } from '../../../../../api/mentions/enum/search-criteria-sort-field.enum';
import { Mention } from '../../../../../Common/Mentions/models/mention';
import { MentionsSearchResponseModel } from '../../../../../api/mentions/model/mentions-search-response.model';
import { SearchCriteriaModel } from '../../../../../api/mentions/model/search-criteria.model';
import { ReportSeriesModel } from '../../../../../Common/Reports/models/report-series.model';
import { NeoFacetDateFieldApiEnum } from '../../../../../api/mentions/model/neo-facet-date-field-api.enum';
import { DataPointModel } from '../../../../../Common/Charts/models/data-point.model';
import { NeoFacetRequestApiModel } from '../../../../../api/mentions/model/neo-facet-request-api.model';
import { BucketFunctionApiEnum } from '../../../../../api/mentions/enum/bucket-function-api.enum';
import { topContactsNeoFacetRequest, topOutletsNeoFacetRequest } from '@Common/Reports/models/report-neo-facet-requests.model';


@Injectable()
export class MADSentimentService extends MADChartsService {

    readonly MADSentimentSeriesConfig: IndividualReportSeriesConfig[] = [

        // Social Sentiment Trend
        {
            type: MADSentimentSeriesTypesEnum.trend,
            facetGenerator: () => {
                return {
                    facet:  {
                        dateField: NeoFacetDateFieldApiEnum.CREATED_AT,
                        key: ReportsNeoFacetKeys.sentimentOverTime,
                        plainBucketRequests: [
                            {
                                key: ReportsNeoFacetKeys.sentimentTrend,
                                field: NeoFacetTermFieldApiEnum.SENTIMENT
                            }
                        ]
                    },
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                return this.reportsSeriesDataMapperService.mapSentimentDateDataFromNeoFacet(response, series);
            },
        },
        {
            type: MADSentimentSeriesTypesEnum.positive,
            label: `Monitor.MAD.sentiment.${MADSentimentSeriesTypesEnum.positive}.label`,
            facetGenerator: () => {
                return {
                    facet: [MentionSearchFacetTypesEnum.sentiment],
                    identifier: ReportSeriesFacetGeneratorEnum.facet
                };
            },
            dataMapper: getSentimentMapper(SearchCriteriaQuerySentimentsEnum.positive)
        },
        {
            type: MADSentimentSeriesTypesEnum.positivePreviousPeriod,
            isPreviousPeriod: true,
            facetGenerator: () => {
                return {
                    facet: [MentionSearchFacetTypesEnum.sentiment],
                    identifier: ReportSeriesFacetGeneratorEnum.facet
                };
            },
            dataMapper: getSentimentMapper(SearchCriteriaQuerySentimentsEnum.positive)
        },
        {
            type: MADSentimentSeriesTypesEnum.neutral,
            label: `Monitor.MAD.sentiment.${MADSentimentSeriesTypesEnum.neutral}.label`,
            facetGenerator: () => {
                return {
                    facet: [MentionSearchFacetTypesEnum.sentiment],
                    identifier: ReportSeriesFacetGeneratorEnum.facet
                };
            },
            dataMapper: getSentimentMapper(SearchCriteriaQuerySentimentsEnum.neutral)
        },
        {
            type: MADSentimentSeriesTypesEnum.neutralPreviousPeriod,
            isPreviousPeriod: true,
            facetGenerator: () => {
                return {
                    facet: [MentionSearchFacetTypesEnum.sentiment],
                    identifier: ReportSeriesFacetGeneratorEnum.facet
                };
            },
            dataMapper: getSentimentMapper(SearchCriteriaQuerySentimentsEnum.neutral)
        },
        {
            type: MADSentimentSeriesTypesEnum.negative,
            label: `Monitor.MAD.sentiment.${MADSentimentSeriesTypesEnum.negative}.label`,
            facetGenerator: () => {
                return {
                    facet: [MentionSearchFacetTypesEnum.sentiment],
                    identifier: ReportSeriesFacetGeneratorEnum.facet
                };
            },
            dataMapper: getSentimentMapper(SearchCriteriaQuerySentimentsEnum.negative)
        },
        {
            type: MADSentimentSeriesTypesEnum.negativePreviousPeriod,
            isPreviousPeriod: true,
            facetGenerator: () => {
                return {
                    facet: [MentionSearchFacetTypesEnum.sentiment],
                    identifier: ReportSeriesFacetGeneratorEnum.facet
                };
            },
            dataMapper: getSentimentMapper(SearchCriteriaQuerySentimentsEnum.negative)
        },
        {
            type: MADSentimentSeriesTypesEnum.sentimentByMediaType,
            facetGenerator: () => {
                return {
                    facet: {
                        key: ReportsNeoFacetKeys.sentimentByMediaType,
                        termField: NeoFacetTermFieldApiEnum.MEDIA_TYPE,
                        plainBucketRequests: [
                            {
                                key: ReportsNeoFacetKeys.sentimentByMediaType,
                                field: NeoFacetTermFieldApiEnum.SENTIMENT
                            }
                        ]
                    },
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                const data = [];
                const buckets = response.neoFacetResults.find(x => x.key === ReportsNeoFacetKeys.sentimentByMediaType)?.buckets;
                if (!buckets) {
                    return data;
                }
                buckets.forEach(x => {
                    const bucket = x.buckets;
                    let mediaType = x.termValue as string;
                    // There is currently a backend issue that return incorrect media types
                    // These will be removed when the backend is fixed
                    if (mediaType === 'NEWS') {
                        mediaType = MediaTypeEnum.ONLINE_NEWS;
                    }
                    if (mediaType === 'BROADCAST') {
                        return;
                    }
                    if (mediaType === 'TWEET') {
                        mediaType = MediaTypeEnum.TWITTER;
                    }
                    if (mediaType === 'BLOG') {
                        mediaType = MediaTypeEnum.BLOGS;
                    }
                    const sentiments = {
                        [SearchCriteriaQuerySentimentsEnum.positive]: 0,
                        [SearchCriteriaQuerySentimentsEnum.neutral]: 0,
                        [SearchCriteriaQuerySentimentsEnum.negative]: 0,
                        [SearchCriteriaQuerySentimentsEnum.notAvailable]: 0
                    };
                    const sentimentResults = bucket[ReportsNeoFacetKeys.sentimentByMediaType];
                    if (!sentimentResults) {
                        return;
                    }
                    (sentimentResults as BucketResponseModel[]).forEach(sentiment => {
                        if (+sentiment.termValue >= 0.3) {
                            sentiments[SearchCriteriaQuerySentimentsEnum.positive] += sentiment.count;
                        } else if (+sentiment.termValue <= -0.3) {
                            sentiments[SearchCriteriaQuerySentimentsEnum.negative] += sentiment.count;
                        } else {
                            sentiments[SearchCriteriaQuerySentimentsEnum.neutral] += sentiment.count;
                        }
                    });
                    sentiments[SearchCriteriaQuerySentimentsEnum.notAvailable] = x.count - sentiments[SearchCriteriaQuerySentimentsEnum.positive]
                        - sentiments[SearchCriteriaQuerySentimentsEnum.neutral] - sentiments[SearchCriteriaQuerySentimentsEnum.negative];
                    for (const key of Object.keys(sentiments)) {
                        const filter = new MentionSearchCriteriaModel();
                        filter.query.includedMediaTypes = [new MediaTypeModel(mediaType as MediaTypeEnum)];
                        filter.query.sentiments = [new SentimentModel(key as SearchCriteriaQuerySentimentsEnum)];
                        data.push({
                            label: this.localizationService.transform(`MediaType.${mediaType}`),
                            mappingKeys: {
                                mediaType: mediaType,
                                sentiment: key
                            },
                            value: sentiments[key],
                            valueLabel: this.localizationService.transform(`Sentiment.${key}`),
                            percent: parseFloat((safeDivide(sentiments[key], x.count) * 100).toFixed(1)),
                            mentionCount: sentiments[key],
                            viewUrl: series.getMentionSeriesUrl(filter),
                        });
                    }
                });

                return data;
            }
        },
        {
            type: MADSentimentSeriesTypesEnum.positiveMap,
            metric: ChartMetricsEnum.volumeOfMentions,
            facetGenerator: () => MADMapChartCountryNeoFacetRequest,
            asyncDataMapper: (response, request, series) => {
                return this.reportsSeriesDataMapperService.mapLocationDataFromNeoFacet(response, series);
            }
        },
        {
            type: MADSentimentSeriesTypesEnum.negativeMap,
            metric: ChartMetricsEnum.volumeOfMentions,
            facetGenerator: () => MADMapChartCountryNeoFacetRequest,
            asyncDataMapper: (response, request, series) => {
                return this.reportsSeriesDataMapperService.mapLocationDataFromNeoFacet(response, series);
            }
        },
        {
            type: MADSentimentSeriesTypesEnum.topMentions,
            mentionProvider: new BehaviorSubject<Mention[]>(null),
            mentionRequestGenerator: () => {
                return {
                    sortOrder: SortOrderEnum.descending,
                    sortField: SearchCriteriaSortFieldEnum.audience_reach,
                    pageSize: 5
                };
            },
            mentionDataMapper: (response) => {
                return response.mentions.map(apiMention => new Mention(apiMention));
            }
        },
        {
            type: MADSentimentSeriesTypesEnum.positiveTopTerms,
            mentionProvider: new BehaviorSubject<Mention[]>(null),
            dataMapper: (response, request, series) => {
                return this.wordCloudDataMapper(response, request, series);
            },
            mentionRequestGenerator: () => {
                return {
                    sortOrder: SortOrderEnum.descending,
                    sortField: SearchCriteriaSortFieldEnum.audience_reach,
                    pageSize: 100,
                    isWordCloudRequest: true
                };
            }
        },
        {
            type: MADSentimentSeriesTypesEnum.negativeTopTerms,
            mentionProvider: new BehaviorSubject<Mention[]>(null),
            dataMapper: (response, request, series) => {
                return this.wordCloudDataMapper(response, request, series);
            },
            mentionRequestGenerator: () => {
                return {
                    sortOrder: SortOrderEnum.descending,
                    sortField: SearchCriteriaSortFieldEnum.audience_reach,
                    pageSize: 100,
                    isWordCloudRequest: true
                };
            }
        },
        {
            type: MADSentimentSeriesTypesEnum.positiveWordCloud,
            mentionProvider: new BehaviorSubject<Mention[]>(null),
            dataMapper: (response, request, series) => {
                return this.wordCloudDataMapper(response, request, series);
            },
            mentionRequestGenerator: () => {
                return {
                    sortOrder: SortOrderEnum.descending,
                    sortField: SearchCriteriaSortFieldEnum.relevancy,
                    pageSize: 100,
                    isWordCloudRequest: true
                };
            }
        },
        {
            type: MADSentimentSeriesTypesEnum.negativeWordCloud,
            mentionProvider: new BehaviorSubject<Mention[]>(null),
            dataMapper: (response, request, series) => {
                return this.wordCloudDataMapper(response, request, series);
            },
            mentionRequestGenerator: () => {
                return {
                    sortOrder: SortOrderEnum.descending,
                    sortField: SearchCriteriaSortFieldEnum.relevancy,
                    pageSize: 100,
                    isWordCloudRequest: true
                };
            }
        },
        {
            type: MADSentimentSeriesTypesEnum.topMentionsSummary,
            metrics: [ChartMetricsEnum.volumeOfMentions, ChartMetricsEnum.IMPRESSIONS, ChartMetricsEnum.ave],
            facetGenerator: () => {
                const facetRequest = JSON.parse(JSON.stringify(summaryTrendNeoFacetRequest));
                return {
                    facet: facetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                return this.reportsSeriesDataMapperService.mapDateDataFromNeoFacet(response, series);
            }
        },
        {
            type: MADSentimentSeriesTypesEnum.positiveOutlets,
            metrics: [ChartMetricsEnum.volumeOfMentions, ChartMetricsEnum.IMPRESSIONS, ChartMetricsEnum.ave, ChartMetricsEnum.domainAuthority],
            facetGenerator: () => {
                return {
                    facet: topOutletsNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                return this.reportsSeriesDataMapperService.mapTop10DataFromNeoFacet(response, series);
            }
        },
        {
            type: MADSentimentSeriesTypesEnum.negativeOutlets,
            metrics: [ChartMetricsEnum.volumeOfMentions, ChartMetricsEnum.IMPRESSIONS, ChartMetricsEnum.ave, ChartMetricsEnum.domainAuthority],
            facetGenerator: () => {
                return {
                    facet: topOutletsNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                return this.reportsSeriesDataMapperService.mapTop10DataFromNeoFacet(response, series);
            }
        },
        {
            type: MADSentimentSeriesTypesEnum.positiveAuthors,
            metrics: [ChartMetricsEnum.volumeOfMentions, ChartMetricsEnum.IMPRESSIONS, ChartMetricsEnum.ave, ChartMetricsEnum.domainAuthority],
            facetGenerator: () => {
                return {
                    facet: topContactsNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                return this.reportsSeriesDataMapperService.mapTop10DataFromNeoFacet(response, series);
            }
        },
        {
            type: MADSentimentSeriesTypesEnum.negativeAuthors,
            metrics: [ChartMetricsEnum.volumeOfMentions, ChartMetricsEnum.IMPRESSIONS, ChartMetricsEnum.ave, ChartMetricsEnum.domainAuthority],
            facetGenerator: () => {
                return {
                    facet: topContactsNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                return this.reportsSeriesDataMapperService.mapTop10DataFromNeoFacet(response, series);
            }
        }
    ];

    constructor(
        public localizationService: LocalizationService,
        public reportsSeriesMapperService: ReportsSeriesMapperService,
        public reportsSeriesDataMapperService: ReportsSeriesDataMapperService,
        public routingService: MonitoringRoutingService,
        public journodexSearchService: JournodexRegionsService
    ) {
        super(routingService, localizationService, journodexSearchService);
        this.setChartConfig(this.MADSentimentSeriesConfig);
    }

    wordCloudDataMapper(response: MentionsSearchResponseModel, request?: SearchCriteriaModel, series?: ReportSeriesModel): DataPointModel[] {
        const values = response.wordCloudResults[0]?.scores.slice(0, 10) ?? [];
        return values.map((val, i) => {
            const filterOverrides = series.mentions.filters.searchCriteria.clone();
            filterOverrides.query.keywordGroups[0].and.push(val.term);

            return {
                id: i + '',
                label: val.term,
                count: val.score,
                tag: val.term,
                'viewMentionUrl': series.getSocialSeriesUrl(filterOverrides, null, true)
            };
        });
    }

}

const summaryTrendNeoFacetRequest: NeoFacetRequestApiModel = {
    dateField: NeoFacetDateFieldApiEnum.CREATED_AT,
    key: ReportsNeoFacetKeys.overtimeFacet,
    aggregateBucketRequests: [
        {
            key: ReportsNeoFacetKeys.impressionsOverTimeFacet,
            field: NeoFacetTermFieldApiEnum.AUDIENCE_REACH,
            function: BucketFunctionApiEnum.INDIVIDUAL_SUM
        },
        {
            key: ReportsNeoFacetKeys.aveOverTimeFacet,
            field: NeoFacetTermFieldApiEnum.AD_VALUE_USD,
            function: BucketFunctionApiEnum.INDIVIDUAL_SUM
        }
    ]
};


